import React from 'react';
import { GridColumnMenuFilter, GridColumnMenuProps, GridColumnMenuSort } from '@progress/kendo-react-grid';
import { GridColumnMenuCheckboxFilterWrapper } from './GridColumnMenuCheckboxFilterWrapper';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { NumericColumnMenuFilter } from './NumericColumnMenuFilter';
import { BulkColumnMenuFilter } from './BulkColumnMenuFilter';
import { CpsFacetValue } from '@cs-cms/types';
import { gridFilterOperators } from '../helpers/gridFilterOperators';

interface ColumnMenuProps extends GridColumnMenuProps {
  field: string;
  fieldFacets: CpsFacetValue[] | undefined;
  isNumericColumn?: boolean;
  supportsBulkFilter?: boolean;
}

export const ColumnMenu: React.FC<ColumnMenuProps> = props => {
  const { field, fieldFacets, filter, isNumericColumn, sortable, supportsBulkFilter } = props;
  const [filterExpanded, setFilterExpanded] = React.useState<boolean>(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const repositionMenuWithinViewport = React.useCallback((): void => {
    const rootElement = rootRef.current;
    if (!rootElement) {
      return;
    }

    const animationContainer = rootElement.closest('.k-animation-container') as HTMLElement | null;
    if (!animationContainer) {
      return;
    }

    const rect = animationContainer.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    const spaceBeforeEdgeInPx = 8;

    const overflowRight = rect.right - (viewportWidth - spaceBeforeEdgeInPx);
    const overflowLeft = spaceBeforeEdgeInPx - rect.left;

    const computedLeft = Number.parseFloat(window.getComputedStyle(animationContainer).left);
    const currentLeft = Number.isFinite(computedLeft) ? computedLeft : 0;

    if (overflowRight > 0) {
      animationContainer.style.left = `${currentLeft - overflowRight}px`;
      return;
    }

    if (overflowLeft > 0) {
      animationContainer.style.left = `${currentLeft + overflowLeft}px`;
    }
  }, []);

  React.useEffect(() => {
    const activeFilter = filter?.filters.some(f1 =>
      (f1 as CompositeFilterDescriptor).filters.some(f2 => (f2 as FilterDescriptor).field === field)
    );
    if (activeFilter && !filterExpanded) {
      setFilterExpanded(true);
    }
  }, [field, filter, filterExpanded]);

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      repositionMenuWithinViewport();
    }, 0);

    window.addEventListener('resize', repositionMenuWithinViewport);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', repositionMenuWithinViewport);
    };
  }, [filterExpanded, repositionMenuWithinViewport]);

  return (
    <div ref={rootRef}>
      {sortable && (
        <GridColumnMenuSort
          column={props.column}
          sort={props.sort}
          onSortChange={props.onSortChange}
          onCloseMenu={props.onCloseMenu}
        />
      )}
      {supportsBulkFilter ? (
        <BulkColumnMenuFilter
          column={props.column}
          columnsState={props.columnsState}
          onColumnsStateChange={props.onColumnsStateChange}
          sort={props.sort}
          filter={props.filter}
          filterOperators={props.filterOperators}
          onSortChange={props.onSortChange}
          onFilterChange={props.onFilterChange}
          onCloseMenu={props.onCloseMenu}
          field={field}
          expanded={filterExpanded}
          onExpandChange={setFilterExpanded}
        />
      ) : fieldFacets ? (
        <GridColumnMenuCheckboxFilterWrapper
          column={props.column}
          columnsState={props.columnsState}
          onColumnsStateChange={props.onColumnsStateChange}
          sort={props.sort}
          filter={props.filter}
          filterOperators={props.filterOperators}
          onSortChange={props.onSortChange}
          onFilterChange={props.onFilterChange}
          onCloseMenu={props.onCloseMenu}
          field={field}
          fieldFacets={fieldFacets}
          expanded={filterExpanded}
          onExpandChange={setFilterExpanded}
        />
      ) : (
        <GridColumnMenuFilter
          column={props.column}
          filter={props.filter}
          onFilterChange={props.onFilterChange}
          onCloseMenu={props.onCloseMenu}
          hideSecondFilter={true}
          filterOperators={gridFilterOperators}
          expanded={filterExpanded}
          onExpandChange={setFilterExpanded}
          filterUI={isNumericColumn ? NumericColumnMenuFilter : undefined}
          filterable={true}
        />
      )}
    </div>
  );
};
